import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/site/Logo";
import { COMPANY, SERVICES } from "@/lib/constants";

const NAV = [
    { label: "Leistungen", to: "/leistungen", hasDropdown: true },
    { label: "Über uns", to: "/ueber-uns" },
    { label: "Ablauf", to: "/ablauf" },
    { label: "Einsatzgebiet", to: "/einsatzgebiet" },
    { label: "FAQ", to: "/faq" },
    { label: "Kontakt", to: "/kontakt" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkClasses = ({ isActive }) =>
        `px-3 py-2 text-sm rounded-md transition-colors ${
            isActive
                ? "text-white bg-white/[0.06]"
                : "text-[#C9D3E1] hover:text-white hover:bg-white/[0.04]"
        }`;

    return (
        <header
            className={`fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter,border-color] duration-300 ${
                scrolled
                    ? "bg-[rgba(7,9,12,0.78)] backdrop-blur-md border-b border-white/[0.08]"
                    : "bg-transparent border-b border-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Logo />

                <nav
                    className="hidden lg:flex items-center gap-0.5"
                    aria-label="Hauptnavigation"
                >
                    {NAV.map((item) =>
                        item.hasDropdown ? (
                            <DropdownMenu key={item.to}>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        className="px-3 py-2 text-sm rounded-md text-[#C9D3E1] hover:text-white hover:bg-white/[0.04] transition-colors inline-flex items-center gap-1"
                                        data-testid={`nav-link-${item.label.toLowerCase()}`}
                                    >
                                        {item.label}
                                        <ChevronDown className="h-3.5 w-3.5" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="start"
                                    className="bg-[#0E141F] border-white/[0.10] text-white min-w-[260px]"
                                >
                                    <DropdownMenuItem
                                        asChild
                                        className="focus:bg-[rgba(46,168,255,0.10)]"
                                    >
                                        <Link to="/leistungen">Alle Leistungen</Link>
                                    </DropdownMenuItem>
                                    {SERVICES.map((s) => (
                                        <DropdownMenuItem
                                            key={s.slug}
                                            asChild
                                            className="focus:bg-[rgba(46,168,255,0.10)]"
                                        >
                                            <Link to={`/leistungen/${s.slug}`}>
                                                {s.title}
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end
                                className={linkClasses}
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {item.label}
                            </NavLink>
                        ),
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        data-testid="header-call-button"
                        className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-[#2EA8FF] text-[#07090C] font-medium hover:bg-[#7BD3FF] transition-colors btn-glow"
                    >
                        <Phone className="h-4 w-4" />
                        <span className="font-mono text-sm">{COMPANY.phone}</span>
                    </a>
                </div>

                <div className="lg:hidden flex items-center gap-2">
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        aria-label="Jetzt anrufen"
                        className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#2EA8FF] text-[#07090C] hover:bg-[#7BD3FF] transition-colors"
                        data-testid="header-call-button-mobile"
                    >
                        <Phone className="h-4 w-4" />
                    </a>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-[#C9D3E1] hover:text-white hover:bg-white/[0.06]"
                                aria-label="Menü öffnen"
                                data-testid="mobile-menu-button"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="bg-[#07090C] border-l border-white/[0.08] text-[#F4F7FB] w-[86%] sm:w-[380px] overflow-y-auto"
                        >
                            <SheetTitle className="sr-only">Navigation</SheetTitle>
                            <SheetDescription className="sr-only">
                                Hauptnavigation der Alpen Reinigung Website
                            </SheetDescription>
                            <div className="flex items-center justify-start mb-6">
                                <Logo />
                            </div>
                            <nav
                                className="flex flex-col gap-1"
                                aria-label="Mobile Navigation"
                            >
                                {NAV.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `px-3 py-3 rounded-md text-base transition-colors ${
                                                isActive
                                                    ? "text-white bg-white/[0.06]"
                                                    : "text-[#C9D3E1] hover:text-white hover:bg-white/[0.05]"
                                            }`
                                        }
                                        data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}
                            </nav>
                            <div className="shimmer-divider my-5" />
                            <div className="px-3 mb-2 text-xs font-mono uppercase tracking-[0.18em] text-[#7BD3FF]">
                                Leistungen im Detail
                            </div>
                            <ul className="flex flex-col">
                                {SERVICES.map((s) => (
                                    <li key={s.slug}>
                                        <Link
                                            to={`/leistungen/${s.slug}`}
                                            onClick={() => setOpen(false)}
                                            className="block px-3 py-2 rounded-md text-sm text-[#C9D3E1] hover:text-white hover:bg-white/[0.05]"
                                        >
                                            {s.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <div className="shimmer-divider my-5" />
                            <a
                                href={`tel:${COMPANY.phoneTel}`}
                                className="flex items-center justify-center gap-2 h-12 rounded-xl bg-[#2EA8FF] text-[#07090C] font-medium hover:bg-[#7BD3FF] transition-colors btn-glow"
                                data-testid="mobile-menu-call-button"
                            >
                                <Phone className="h-4 w-4" />
                                <span className="font-mono text-sm">{COMPANY.phone}</span>
                            </a>
                            <a
                                href={`mailto:${COMPANY.email}`}
                                className="mt-3 flex items-center justify-center h-11 rounded-xl border border-white/[0.12] text-[#C9D3E1] hover:text-white hover:border-[rgba(123,211,255,0.5)] transition-colors"
                                data-testid="mobile-menu-email"
                            >
                                {COMPANY.email}
                            </a>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
